<script setup lang="ts">
import { useRouteParams } from '@vueuse/router';

import { useCompanyStore } from '@sub/stores/company';
import SlotsCalendar from '@sub/components/calendar/SlotsCalendar.vue';
import ServiceOrder from '@sub/components/service/ServiceOrder.vue';
import type { IService } from '@types';
import { useOrderStore } from '@sub/stores/order';
import WarningModal from '@sub/components/service/WarningModal.vue';
import { useOrderedTimeSlots } from '@sub/components/calendar/useOrderedTimeSlots';
import TargetSelector from '@sub/components/booking/TargetSelector.vue';
import { joinPageTitle, usePageTitle } from '@sub/utils/usePageSettings';
import { useOrderWarningModal } from '@sub/components/service/useOrderWarningModal';

const store = useCompanyStore();
const { company, companyInfo, services } = storeToRefs(store);

const orderStore = useOrderStore();
const { order, groupSlotAvailability } = storeToRefs(orderStore);

const routerServiceId = useRouteParams<number>('id', services.value.main[0]?.id, { transform(val) {
    const paramID = +val;
    if (services.value.main.length && !services.value.main.some(s => s.id === paramID)) {
      const i = services.value.main.length > 1 ? 1 : 0;
      return services.value.main[i].id as number;
    }
    return paramID;
}, });

const { serviceId, showWarning, keepService, updateService: _updateService } = useOrderWarningModal(routerServiceId, order);

const { orderedTimeSlots, pendingSlots } = useOrderedTimeSlots(serviceId);

const { status } = useAsyncData(async() => {
  const companyId = await store.getCompanyIdByAlias();
  if (!companyId) return;
  return Promise.all([
    store.fetchCompany(companyId),
    store.fetchServices(companyId),
  ]);
});

function updateService() {
  orderStore.clear();
  _updateService();
}

const service = computed<IService>(() => services.value.main.find(s => s.id === serviceId.value) || {} as IService);

const i18n = useI18n();
usePageTitle(computed(() => joinPageTitle(i18n.t('page.booking.service.header.title'), service.value?.title)));

watch(service, () => {
  if (service.value) {
    orderStore.clear();
    orderStore.updateOrderService(service.value);
  }
}, { immediate: true });

definePageMeta({
  keepalive: false,
});

const showViewer = ref(false);
const activeImages = ref([]);

function onOpenViewer(images: string[]) {
  if (!images.length) return;
  activeImages.value = images;
  showViewer.value = true;
}

function resetOnBack() {
  orderStore.resetOrderTarget();
  setTimeout(orderStore.clearDates);
}
</script>

<template lang="pug">
template(v-if="company && Object.keys(company).length")
  .mobile-title.typ-14-18-500.p-12-16.text-center.ellipsis.z-index-5 {{ service.title }}
  ServicePageHeader(label="page.booking.service.header.title" :back="order.target ? `/booking/${service.id}` : '/services'" :logoSrc="company.logoUrl" @back="resetOnBack")
  ServiceScrollSelector.selector(
    v-if="services.main.length > 1"
    v-model:serviceId="routerServiceId"
    :class="{show: !service.targets?.length || order.target}"
    :services="services.main"
    :defaultImage="company.logoUrl"
    @open:images="onOpenViewer"
  )
  template(v-if="service.targets?.length && !order.target")
    TargetSelector(:targets="service.targets" :type="service.type" :title="service.title" @update:target="orderStore.setOrderTarget")
  template(v-else)
    UiLoaderCircleLoader.m-auto(v-if="status === 'pending' || pendingSlots" title="page.booking.service.loader")
    .calendar-container.flex.w-100p(v-else :class="{services: services.main.length > 1}")
      SlotsCalendar(
        v-model:selected="order.dates"
        :company="company"
        :service="service"
        :target="order.target"
        :orderedTimeSlots="orderedTimeSlots"
        :showTimezone="companyInfo.settings.showTimezone"
        :timeslotSettings="companyInfo.settings.timeslot"
        @update:slotAvailability="groupSlotAvailability = $event"
      )
      .service-order-container
        ServiceOrder(:order="order" :slotAvailability="groupSlotAvailability")
  UiImageViewer(v-model:show="showViewer" :src="activeImages")
  WarningModal(v-model:show="showWarning" maxWidth="328px" :order="order" @order:keep="keepService" @order:update="updateService")
</template>

<style lang="scss" scoped>
main {
  position: relative;
}

.calendar-container {
  @screen md {
    --uno: overflow-hidden;
    max-height: max(500px, calc(100vh - 156px));
    &.services {
      max-height: max(500px, calc(100vh - 273px));
    }
  }
}

.selector {
  visibility: hidden;
  height: 0;
  &.show {
    visibility: unset;
    height: unset;
  }
}

.mobile-title {
  background: #F8F8F9;
  color: #1f1f1f;
  position: sticky;
  margin-top: -42px;
  top: 0;

  @screen md {
    display: none;
  }
}

.service-order-container {
  --uno: position-fixed b-20 l-16 r-16 z-index-1;

  @screen md {
    --uno: position-unset p-14-8;
  }
}
</style>
